import {
  DocumentNode,
  OperationVariables,
  useFragment,
  useQuery,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';

// Define a basic shape for GraphQL errors returned within the data object
type GraphQLErrorShape = {
  __typename: 'Error';
  message?: string | null;
};

export type StandardRefHookOptions = {
  name: string;
  skip?: boolean;
};

// TItem is the type of the data *at* dataPath
// TPath is the key under which TItem will be returned
type StandardRefHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem | undefined;
} & {
  error?: Error;
  isLoading: boolean;
};

/**
 * Creates a cache-optimized hook for fetching entity references.
 *
 * This HOC leverages Apollo Client's cache by first checking if the required fragment
 * is already cached using useFragment. If the fragment is complete, it returns the
 * cached data immediately without making a network request. Otherwise, it falls back
 * to executing the query.
 *
 * @param queryDoc - The query document to execute if fragment is not in cache
 * @param dataPath - The key in the query result containing the entity
 * @param fragmentDoc - The fragment document to check in the cache
 * @param fragmentName - The name of the fragment
 * @param typename - The __typename of the entity for cache lookup
 */
export function withStandardRefHook<
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  queryDoc: DocumentNode,
  dataPath: TPath,
  fragmentDoc: DocumentNode,
  fragmentName: string,
  typename: string,
): (options: StandardRefHookOptions) => StandardRefHookResult<TItem, TPath> {
  return (
    options: StandardRefHookOptions,
  ): StandardRefHookResult<TItem, TPath> => {
    /**
     * Optimized reference hook that leverages Apollo Client's cache.
     *
     * This hook first attempts to read the fragment from the cache using useFragment.
     * If the fragment is already cached (complete = true), we return it immediately without
     * making a network request. Otherwise, we fall back to the query.
     *
     * Note: Due to React's rules of hooks, we must call both useFragment and useQuery
     * unconditionally, using the 'skip' parameter to control execution flow.
     */
    const { data: fragmentData, complete } = useFragment<TItem>({
      fragment: fragmentDoc,
      fragmentName,
      from: options.skip
        ? null
        : {
            __typename: typename,
            name: options.name,
          },
    });

    const {
      data,
      error: networkError,
      loading,
    } = useQuery<TData, TVariables>(queryDoc, {
      variables: { name: options.name } as TVariables,
      skip: options.skip || complete,
      fetchPolicy: 'cache-first',
    });

    // If fragment is complete in cache, return cached data
    if (complete) {
      return {
        [dataPath]: fragmentData as TItem,
        error: undefined,
        isLoading: false,
      } as StandardRefHookResult<TItem, TPath>;
    }

    // Get the data directly from the path, type is inferred as TItem | null
    const typedData = getTypeOrNull(data?.[dataPath] as any) ?? undefined;
    const appError = getError(data?.[dataPath] as any) ?? undefined;

    return {
      [dataPath]: typedData as TItem | undefined,
      error: appError ?? networkError,
      isLoading: loading,
    } as StandardRefHookResult<TItem, TPath>;
  };
}
